import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AccordionComponent, ListTableComponent } from '@ppl/ui';
import { TimesheetService } from '../../../../services/timesheet.service';
import { UserService } from '../../../../services/user.service';
import { InvoiceService } from 'src/app/services/invoice.service';
import { PurchaseRequestService } from 'src/app/services/purchase-request.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'ppl-auth-utilization-line-details',
  standalone: true,
  imports: [AccordionComponent, ListTableComponent, CommonModule],
  templateUrl: './auth-utilization-line-details.component.html',
  styleUrl: './auth-utilization-line-details.component.css'
})
export class AuthUtilizationLineDetailsComponent implements OnInit, OnChanges {
  mode = 'read';
  programName!: string;
  columnsToBeHidden: string[] = [];
  sharedDataKey!: string;
  actionsToBeHidden: string[] = [];
  lazyLoad = true;
  isPageRestrictionLoaded = false;
  @Input() authData: any;

  /** Each source gets its own grid — the three payloads no longer share a column set. */
  timesheetUtilizationData: any[] = [];
  invoiceUtilizationData: any[] = [];
  purchaseRequestUtilizationData: any[] = [];

  /** Small by design: these grids sit inside the authorization details dialog. */
  readonly recordsPerPage = 5;

  /** Each grid draws its columns from its own list config under assets/.../list_pages. */
  readonly timesheetListConfig = 'list_auth_timesheets';
  readonly invoiceListConfig = 'list_auth_invoices';
  readonly purchaseRequestListConfig = 'list_auth_purchase-requests';

  constructor(
    private timesheetService: TimesheetService,
    private userService: UserService,
    private invoiceService: InvoiceService,
    private purchaseRequestService: PurchaseRequestService,
  ) {}

  ngOnInit() {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.isPageRestrictionLoaded = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['authData'] && changes['authData'].currentValue) {
      this.getUtilizationData();
    }
  }

  getUtilizationData() {
    const authorizationId = this.authData?.authorizationId;
    const participantId = this.authData?.participantId;

    // One failing source must not blank the other two grids, so each falls back to an empty
    // response rather than taking the whole forkJoin down with it.
    forkJoin({
      timesheetData: this.timesheetService
        .getAuthTimesheetUtilization(this.programName, authorizationId, participantId)
        .pipe(catchError(() => of(null))),
      invoiceData: this.invoiceService
        .getAuthInvoiceUtilization(this.programName, authorizationId, participantId)
        .pipe(catchError(() => of(null))),
      purchaseRequestData: this.purchaseRequestService
        .getAuthPurchaseRequestUtilization(this.programName, authorizationId, participantId)
        .pipe(catchError(() => of(null))),
    }).subscribe(({ timesheetData, invoiceData, purchaseRequestData }) => {
      this.timesheetUtilizationData = this.prepareRows(timesheetData?.responsedata);
      this.invoiceUtilizationData = this.prepareRows(invoiceData?.responsedata);
      this.purchaseRequestUtilizationData = this.prepareRows(purchaseRequestData?.responsedata);
    });
  }

  /**
   * Dates arrive as yyyy-MM-dd and are shown US-style. A timesheet line has no serviceDate of
   * its own, so its range start stands in for one.
   */
  private prepareRows(rows: any): any[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row: any) => {
      const prepared = { ...row };

      if (prepared.serviceFromDate) {
        prepared.serviceFromDate = this.formatDateToUSDateString(prepared.serviceFromDate);
      }
      if (prepared.serviceToDate) {
        prepared.serviceToDate = this.formatDateToUSDateString(prepared.serviceToDate);
      }
      if (prepared.serviceDate) {
        prepared.serviceDate = this.formatDateToUSDateString(prepared.serviceDate);
      }
      if (prepared.submissionDate) {
        prepared.submissionDate = this.formatDateToUSDateString(prepared.submissionDate);
      }

      if (!prepared.serviceDate && prepared.serviceFromDate) {
        prepared.serviceDate = `${prepared.serviceFromDate}`;
      }

      return prepared;
    });
  }

  rowClicked(event: unknown) {}

  formatDateToUSDateString(dateString: string): string {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }
}
